import React from "react";
import { Button } from "../../../../design/button";
import { Divider } from "../../../../design/divider";
import {
  DocumentTextIcon,
  UserGroupIcon,
  FaceSmileIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { WizardFormData, RELATIONSHIP_OPTIONS, CUSTODY_STATUS_OPTIONS, REASON_FOR_SUPERVISION_OPTIONS, GENDER_OPTIONS } from "./types";

interface ReviewStepProps {
  formData: WizardFormData;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  onEditStep: (step: number) => void;
  isSubmitting: boolean;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  onBack,
  onSubmit,
  onEditStep,
  isSubmitting,
}) => {
  const getRelationshipLabel = (value: string) => {
    return RELATIONSHIP_OPTIONS.find((o) => o.value === value)?.label || value;
  };

  const getCustodyLabel = (value: string) => {
    return CUSTODY_STATUS_OPTIONS.find((o) => o.value === value)?.label || value;
  };

  const getReasonLabel = (value: string) => {
    return REASON_FOR_SUPERVISION_OPTIONS.find((o) => o.value === value)?.label || value;
  };

  const getGenderLabel = (value: string) => {
    return GENDER_OPTIONS.find((o) => o.value === value)?.label || value;
  };

  const formatAddress = (parent: typeof formData.parents[0]) => {
    const parts = [
      parent.streetAddress,
      parent.city,
      parent.state,
      parent.zipCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Not provided";
  };

  const formatCourtAddress = () => {
    const parts = [
      formData.case.courtBranchName,
      formData.case.courtStreetAddress,
      formData.case.courtCity,
      formData.case.courtZipCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Not provided";
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
          Review Case Details
        </h3>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Please review all information before creating the case
        </p>
      </div>

      {/* Case Details Section */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
        <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800 px-4 py-3">
          <div className="flex items-center space-x-2">
            <DocumentTextIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="font-medium text-zinc-900 dark:text-zinc-100">
              Case Details
            </span>
          </div>
          <Button
            type="button"
            plain
            onClick={() => onEditStep(1)}
            className="text-sm"
          >
            <PencilSquareIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Case Identifier
              </dt>
              <dd className="text-sm text-zinc-900 dark:text-zinc-100">
                {formData.case.identifier || "Not provided"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Court County
              </dt>
              <dd className="text-sm text-zinc-900 dark:text-zinc-100">
                {formData.case.courtCounty || "Not provided"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Reason for Supervision
              </dt>
              <dd className="text-sm text-zinc-900 dark:text-zinc-100">
                {formData.case.reasonForSupervision
                  ? getReasonLabel(formData.case.reasonForSupervision)
                  : "Not provided"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Referral Source
              </dt>
              <dd className="text-sm text-zinc-900 dark:text-zinc-100">
                {formData.case.referralSource || "Not provided"}
              </dd>
            </div>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
              Court Address
            </dt>
            <dd className="text-sm text-zinc-900 dark:text-zinc-100">
              {formatCourtAddress()}
            </dd>
          </div>
          {formData.case.notes && (
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Notes
              </dt>
              <dd className="text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap">
                {formData.case.notes}
              </dd>
            </div>
          )}
        </div>
      </div>

      {/* Parents Section */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
        <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800 px-4 py-3">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <span className="font-medium text-zinc-900 dark:text-zinc-100">
              Parents ({formData.parents.length})
            </span>
          </div>
          <Button
            type="button"
            plain
            onClick={() => onEditStep(2)}
            className="text-sm"
          >
            <PencilSquareIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
        <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {formData.parents.map((parent, index) => (
            <div key={parent.id} className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-zinc-900 dark:text-zinc-100">
                  {parent.firstName} {parent.lastName}
                </span>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">
                  {parent.relationshipToChild
                    ? getRelationshipLabel(parent.relationshipToChild)
                    : "Relationship not specified"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Email: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {parent.email || "Not provided"}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Phone: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {parent.phoneNumber || "Not provided"}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Custody: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {parent.custodyStatus
                      ? getCustodyLabel(parent.custodyStatus)
                      : "Not specified"}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Fee %: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {parent.feePercentage}%
                  </span>
                </div>
              </div>
              <div className="text-sm">
                <span className="text-zinc-500 dark:text-zinc-400">Address: </span>
                <span className="text-zinc-900 dark:text-zinc-100">
                  {formatAddress(parent)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Children Section */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
        <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800 px-4 py-3">
          <div className="flex items-center space-x-2">
            <FaceSmileIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <span className="font-medium text-zinc-900 dark:text-zinc-100">
              Children ({formData.children.length})
            </span>
          </div>
          <Button
            type="button"
            plain
            onClick={() => onEditStep(3)}
            className="text-sm"
          >
            <PencilSquareIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
        <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {formData.children.map((child, index) => (
            <div key={child.id} className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-zinc-900 dark:text-zinc-100">
                  {child.firstName} {child.lastName}
                </span>
                {child.hasSpecialNeeds && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                    Special Needs
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">DOB: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {child.dateOfBirth || "Not provided"}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Gender: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {child.gender ? getGenderLabel(child.gender) : "Not specified"}
                  </span>
                </div>
              </div>
              {child.allergies && (
                <div className="text-sm">
                  <span className="text-zinc-500 dark:text-zinc-400">Allergies: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {child.allergies}
                  </span>
                </div>
              )}
              {child.hasSpecialNeeds && child.specialNeeds && (
                <div className="text-sm">
                  <span className="text-zinc-500 dark:text-zinc-400">
                    Special Needs Details:{" "}
                  </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {child.specialNeeds}
                  </span>
                </div>
              )}
              {child.notes && (
                <div className="text-sm">
                  <span className="text-zinc-500 dark:text-zinc-400">Notes: </span>
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {child.notes}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Divider />

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button type="button" plain onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          type="button"
          color="green"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating Case..." : "Create Case"}
        </Button>
      </div>
    </div>
  );
};

export default ReviewStep;
